/**
 * Pure sub-object splitting logic for .meme files.
 * Mirrors the viewer-side Draco sub-object split so recovery can stay format-parity.
 */

export interface MemeSubObject {
  name: string
  materialName: string | null
  positions: Float32Array
  normals: Float32Array | null
  uvs: Float32Array | null
  indices: Uint32Array
}

export interface FlatMeshData {
  numVertices: number
  numFaces: number
  positions: Float32Array
  normals: Float32Array | null
  uvs: Float32Array | null
  indices: Uint32Array
  subObjectIds: Float32Array
  materialIds: Float32Array | null
  subObjectNames: string[] | null
  materialNames: string[] | null
}

interface VertexRemap {
  subObjectId: number
  localIndex: number
}

export function splitSubObjects(data: FlatMeshData): MemeSubObject[] {
  const numVertices = data.numVertices
  const numFaces = data.numFaces
  const positions = data.positions
  const normals = data.normals
  const uvs = data.uvs
  const indices = data.indices
  const subObjectIds = data.subObjectIds
  const materialIds = data.materialIds
  const subObjectNames = data.subObjectNames
  const materialNames = data.materialNames

  const vertexRemap: VertexRemap[] = new Array(numVertices)
  const subObjectVertexCounts = new Map<number, number>()
  const materialIdForSubObject = new Map<number, number>()

  for (let i = 0; i < numVertices; i++) {
    const subObjectId = Math.round(subObjectIds[i])

    let count = subObjectVertexCounts.get(subObjectId)
    if (count === undefined) {
      count = 0
      subObjectVertexCounts.set(subObjectId, 0)

      if (materialIds !== null) {
        materialIdForSubObject.set(subObjectId, Math.round(materialIds[i]))
      }
    }

    vertexRemap[i] = { subObjectId: subObjectId, localIndex: count }
    subObjectVertexCounts.set(subObjectId, count + 1)
  }

  const faceCounts = new Map<number, number>()
  subObjectVertexCounts.forEach(function (_, id) {
    faceCounts.set(id, 0)
  })

  for (let i = 0; i < numFaces; i++) {
    const idx0 = indices[i * 3]
    const subObjectId = vertexRemap[idx0].subObjectId
    faceCounts.set(subObjectId, (faceCounts.get(subObjectId) || 0) + 1)
  }

  const subObjectPositions = new Map<number, Float32Array>()
  const subObjectNormals = new Map<number, Float32Array | null>()
  const subObjectUvs = new Map<number, Float32Array | null>()
  const subObjectIndices = new Map<number, Uint32Array>()
  const indexWriteOffsets = new Map<number, number>()

  subObjectVertexCounts.forEach(function (vertCount, id) {
    subObjectPositions.set(id, new Float32Array(vertCount * 3))
    subObjectNormals.set(id, normals ? new Float32Array(vertCount * 3) : null)
    subObjectUvs.set(id, uvs ? new Float32Array(vertCount * 2) : null)
    subObjectIndices.set(id, new Uint32Array((faceCounts.get(id) || 0) * 3))
    indexWriteOffsets.set(id, 0)
  })

  for (let i = 0; i < numVertices; i++) {
    const remap = vertexRemap[i]
    const subObjectId = remap.subObjectId
    const localIndex = remap.localIndex

    const dstPos = subObjectPositions.get(subObjectId) as Float32Array
    dstPos[localIndex * 3] = positions[i * 3]
    dstPos[localIndex * 3 + 1] = positions[i * 3 + 1]
    dstPos[localIndex * 3 + 2] = positions[i * 3 + 2]

    if (normals) {
      const dstNrm = subObjectNormals.get(subObjectId) as Float32Array
      dstNrm[localIndex * 3] = normals[i * 3]
      dstNrm[localIndex * 3 + 1] = normals[i * 3 + 1]
      dstNrm[localIndex * 3 + 2] = normals[i * 3 + 2]
    }

    if (uvs) {
      const dstUv = subObjectUvs.get(subObjectId) as Float32Array
      dstUv[localIndex * 2] = uvs[i * 2]
      dstUv[localIndex * 2 + 1] = uvs[i * 2 + 1]
    }
  }

  for (let i = 0; i < numFaces; i++) {
    const idx0 = indices[i * 3]
    const idx1 = indices[i * 3 + 1]
    const idx2 = indices[i * 3 + 2]

    const subObjectId = vertexRemap[idx0].subObjectId
    const dstIdx = subObjectIndices.get(subObjectId) as Uint32Array
    const offset = indexWriteOffsets.get(subObjectId) || 0

    dstIdx[offset] = vertexRemap[idx0].localIndex
    dstIdx[offset + 1] = vertexRemap[idx1].localIndex
    dstIdx[offset + 2] = vertexRemap[idx2].localIndex

    indexWriteOffsets.set(subObjectId, offset + 3)
  }

  const result: MemeSubObject[] = []
  subObjectVertexCounts.forEach(function (_, id) {
    const name = subObjectNames && id < subObjectNames.length
      ? subObjectNames[id]
      : '__untitled__jig__meme' + id

    let materialName: string | null = null
    if (materialNames !== null) {
      const materialId = materialIdForSubObject.get(id)
      if (materialId !== undefined && materialId < materialNames.length) {
        materialName = materialNames[materialId]
      }
    }

    result.push({
      name: name,
      materialName: materialName,
      positions: subObjectPositions.get(id) as Float32Array,
      normals: subObjectNormals.get(id) as (Float32Array | null),
      uvs: subObjectUvs.get(id) as (Float32Array | null),
      indices: subObjectIndices.get(id) as Uint32Array,
    })
  })

  return result
}
